import React from "react";

import { IconComponents, IconExternalLink } from "~/components/icon";

import type { SourceLinkPartType } from "./_part-types";
import type { ComponentViewMode } from "./_use-navigate-to-component";

const SAFE_EXTERNAL_URL_PATTERN = /^https?:\/\//iu;
const isSafeExternalUrl = (url: string): boolean => SAFE_EXTERNAL_URL_PATTERN.test(url.trim());

export const SourceLinkChip: React.FC<{
	part: SourceLinkPartType;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}> = ({ part, onNavigateToComponent }) => {
	const isExternal = part.link_type === "external" || part.link_type === "documentation";
	const identifier = part.urn;
	const canNavigate = !isExternal && identifier != null && onNavigateToComponent != null;
	const safeExternalUrl = isExternal && part.url != null && isSafeExternalUrl(part.url) ? part.url.trim() : undefined;

	if (safeExternalUrl != null) {
		return (
			<a
				className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-gray-400 bg-white px-2.5 py-1 text-gray-800 text-xs shadow-sm transition-colors hover:border-primary-500 hover:text-primary-500"
				href={safeExternalUrl}
				rel="noopener noreferrer"
				target="_blank"
			>
				<IconExternalLink className="size-3" />
				{part.label}
				<span className="sr-only">Opens in new tab</span>
			</a>
		);
	}

	if (canNavigate) {
		return (
			<button
				className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-primary-500 bg-primary-100 px-2.5 py-1 text-primary-700 text-xs shadow-sm transition-colors hover:border-primary-600 hover:bg-white hover:text-primary-600"
				onClick={() => onNavigateToComponent(identifier)}
				type="button"
			>
				<IconComponents className="size-3" />
				{part.label}
			</button>
		);
	}

	return (
		<span className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-100 px-2.5 py-1 text-gray-600 text-xs">
			{part.label}
		</span>
	);
};
